import { useCallback, useEffect, useRef, useState, type DragEvent, type MouseEvent } from "react"
import { toast } from "sonner"
import {
  Background,
  getBezierPath,
  Panel,
  PanOnScrollMode,
  Position,
  ReactFlow,
  ReactFlowProvider,
  SelectionMode,
  useEdgesState,
  useKeyPress,
  useNodesState,
  useReactFlow,
  useStore,
  useStoreApi,
  ViewportPortal,
  type Connection,
  type ConnectionLineComponentProps,
  type Edge,
  type InternalNode,
  type NodeChange,
  type NodePositionChange,
  type OnConnectEnd,
  type OnMove,
} from "@xyflow/react"
import "@xyflow/react/dist/style.css"
import "./flow-canvas.css"
import { TriangleAlert, Workflow } from "lucide-react"
import { FlowAlignmentGuides } from "@/components/flow/flow-alignment-guides"
import type { FlowMenuConnectFrom } from "@/components/flow/flow-context-menu"
import { FlowNodeCard, type FlowCardNode } from "@/components/flow/flow-node-card"
import { FlowProjectContext } from "@/components/flow/flow-project-context"
import { FLOW_MAX_ZOOM, FLOW_MIN_ZOOM, FlowViewportControls } from "@/components/flow/flow-viewport-controls"
import { BoardEmptyState } from "@/components/workspace/board-empty-state"
import { usePasteFiles } from "@/hooks/use-paste-files"
import { useTranslation } from "@/hooks/use-translation"
import { isFileDrag } from "@/lib/filter-files-by-accept"
import {
  flowGroupAlignmentGuides,
  flowResizeSnap,
  type FlowGuide,
  type FlowRect,
  type FlowResizeAnchor,
} from "@/lib/flow-alignment-guides"
import { flowEdgeHandlePoint } from "@/lib/flow-edge-handle-point"
import {
  FLOW_NODE_MAX_LONG_SIDE,
  FLOW_NODE_MIN_LONG_SIDE,
  clampFlowNodeRatio,
  flowNodeAspectRatio,
  flowNodeWidth,
} from "@/lib/flow-node-size"
import { pollAnimationFrames } from "@/lib/poll-animation-frames"
import { cn } from "@/lib/utils"
import { useUiStore } from "@/stores/ui-store"
import {
  flowConnectionAllowed,
  flowConnectPorts,
  flowNodeSourceHandles,
  flowNodeTargetHandles,
  flowResolveNodeDrop,
  flowVisualTargetHandle,
  type FlowNodeRow,
  type FlowOpenLayout,
} from "@/types/flow"

const nodeTypes = { flowCard: FlowNodeCard }

function edgePointOnNode(
  node: {
    internals: {
      positionAbsolute: { x: number; y: number }
      handleBounds?: {
        source: { id?: string | null; x: number; y: number; width: number; height: number; position: Position }[] | null
        target: { id?: string | null; x: number; y: number; width: number; height: number; position: Position }[] | null
      }
    }
  },
  side: "source" | "target",
  port: string | null | undefined,
  fallbackToFirst = false,
): { x: number; y: number; position: Position } | null {
  const bounds = node.internals.handleBounds?.[side]
  const matched = port ? bounds?.find((item) => item.id === port) : bounds?.[0]
  const handle = matched ?? (fallbackToFirst ? bounds?.[0] : undefined)
  if (!handle) return null
  return flowEdgeHandlePoint(node.internals.positionAbsolute, handle)
}

function FlowConnectPreviewLine({ preview }: { preview: FlowConnectPreview }) {
  const node = useStore((state) => state.nodeLookup.get(String(preview.sourceId)))
  const source = node ? edgePointOnNode(node, preview.side, preview.sourcePort, true) : null
  if (!source) return null
  const fromInput = preview.side === "target"
  const [path] = getBezierPath({
    sourceX: source.x,
    sourceY: source.y,
    sourcePosition: source.position,
    targetX: preview.x,
    targetY: preview.y,
    targetPosition: fromInput ? Position.Right : Position.Left,
  })
  return (
    <ViewportPortal>
      <svg className="pointer-events-none absolute top-0 left-0 overflow-visible" style={{ zIndex: 1000 }} aria-hidden>
        <path d={path} fill="none" stroke="var(--ui-accent)" strokeWidth={2} />
      </svg>
    </ViewportPortal>
  )
}

export type FlowConnectPreview = {
  sourceId: number
  sourcePort: string
  x: number
  y: number
  /** Ручка существующей ноды: выход справа, вход слева. */
  side: "source" | "target"
}

/** Зазор рамки от карточек и от подписи. */
const FLOW_SELECTION_PAD = 12
/** Подпись над карточкой: `h-4` + `mb-1` в flow-node-title. */
const FLOW_SELECTION_TITLE = 20

type FlowSelectionFrameRect = {
  x: number
  y: number
  width: number
  height: number
}

function selectionFrameRect(
  nodeLookup: Map<string, InternalNode>,
  userSelectionActive: boolean,
): FlowSelectionFrameRect | null {
  if (userSelectionActive) return null
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  let count = 0
  for (const node of nodeLookup.values()) {
    if (!node.selected || node.hidden) continue
    const width = node.measured?.width ?? node.width
    const height = node.measured?.height ?? node.height
    if (width == null || height == null) continue
    const { x, y } = node.internals.positionAbsolute
    minX = Math.min(minX, x)
    minY = Math.min(minY, y)
    maxX = Math.max(maxX, x + width)
    maxY = Math.max(maxY, y + height)
    count += 1
  }
  if (count < 2) return null
  return {
    x: minX - FLOW_SELECTION_PAD,
    y: minY - FLOW_SELECTION_TITLE - FLOW_SELECTION_PAD,
    width: maxX - minX + FLOW_SELECTION_PAD * 2,
    height: maxY - minY + FLOW_SELECTION_TITLE + FLOW_SELECTION_PAD * 2,
  }
}

function sameSelectionFrame(left: FlowSelectionFrameRect | null, right: FlowSelectionFrameRect | null): boolean {
  if (left === right) return true
  if (!left || !right) return false
  return left.x === right.x && left.y === right.y && left.width === right.width && left.height === right.height
}

function FlowSelectionFrame() {
  const frame = useStore((state) => selectionFrameRect(state.nodeLookup, state.userSelectionActive), sameSelectionFrame)
  if (!frame) return null
  return (
    <ViewportPortal>
      <div
        className="flow-selection-frame pointer-events-none absolute"
        style={{ left: frame.x, top: frame.y, width: frame.width, height: frame.height }}
        aria-hidden
      />
    </ViewportPortal>
  )
}

function FlowConnectionLine({
  fromX,
  fromY,
  toX,
  toY,
  fromPosition,
  toPosition,
  fromNode,
  fromHandle,
  toNode,
  toHandle,
  connectionStatus,
}: ConnectionLineComponentProps<FlowCardNode>) {
  const nodeLookup = useStore((state) => state.nodeLookup)
  const source =
    fromNode && (fromHandle?.type === "source" || fromHandle?.type === "target")
      ? edgePointOnNode(fromNode, fromHandle.type, fromHandle.id)
      : null
  const validTarget =
    toNode && connectionStatus === "valid" && (toHandle?.type === "source" || toHandle?.type === "target")
      ? edgePointOnNode(toNode, toHandle.type, toHandle.id)
      : null
  const snapped =
    validTarget ??
    (toNode && connectionStatus === "valid"
      ? null
      : connectionSnapEnd(nodeLookup as Map<string, InternalNode<FlowCardNode>>, fromNode, fromHandle, toX, toY))
  const [path] = getBezierPath({
    sourceX: source?.x ?? fromX,
    sourceY: source?.y ?? fromY,
    sourcePosition: source?.position ?? fromPosition,
    targetX: snapped?.x ?? toX,
    targetY: snapped?.y ?? toY,
    targetPosition: snapped?.position ?? toPosition,
  })
  return <path d={path} fill="none" className="react-flow__connection-path" />
}

function connectionSnapEnd(
  lookup: Map<string, InternalNode<FlowCardNode>>,
  fromNode: InternalNode<FlowCardNode>,
  fromHandle: ConnectionLineComponentProps<FlowCardNode>["fromHandle"],
  x: number,
  y: number,
): { x: number; y: number; position: Position } | null {
  const handleId = fromHandle.id
  if (!handleId || (fromHandle.type !== "source" && fromHandle.type !== "target")) return null
  const rows: FlowNodeRow[] = []
  let best: { node: InternalNode<FlowCardNode>; score: number } | null = null
  let index = 0
  for (const node of lookup.values()) {
    index += 1
    rows.push(node.data)
    if (node.id === fromNode.id) continue
    const width = node.measured?.width ?? node.width ?? 0
    const height = node.measured?.height ?? 0
    if (width <= 0 || height <= 0) continue
    const left = node.internals.positionAbsolute.x
    const top = node.internals.positionAbsolute.y
    if (x < left || y < top || x > left + width || y > top + height) continue
    const score = (node.selected ? 1_000_000 : 0) + index
    if (!best || score >= best.score) best = { node, score }
  }
  if (!best) return null
  const resolved = flowResolveNodeDrop(
    rows,
    { id: Number(fromNode.id), handleType: fromHandle.type, handleId },
    Number(best.node.id),
  )
  if (!resolved) return null
  const side = fromHandle.type === "source" ? "target" : "source"
  const port = side === "target" ? resolved.targetHandle : resolved.sourceHandle
  return edgePointOnNode(best.node, side, port)
}

function connectEndClientPoint(event: globalThis.MouseEvent | globalThis.TouchEvent): { x: number; y: number } | null {
  if ("changedTouches" in event) {
    const touch = event.changedTouches[0]
    if (!touch) return null
    return { x: touch.clientX, y: touch.clientY }
  }
  return { x: event.clientX, y: event.clientY }
}

function flowNodeIdAtPoint(clientX: number, clientY: number): number | null {
  for (const element of document.elementsFromPoint(clientX, clientY)) {
    const node = element.closest(".react-flow__node")
    if (!node) continue
    const id = Number(node.getAttribute("data-id"))
    return Number.isFinite(id) ? id : null
  }
  return null
}

function connectMenuFromHandle(
  row: FlowNodeRow,
  handleType: "source" | "target",
  handleId: string,
): FlowMenuConnectFrom | null {
  const handles = handleType === "source" ? flowNodeSourceHandles(row) : flowNodeTargetHandles(row)
  if (!handles.includes(handleId)) return null
  return {
    nodeId: row.id,
    port: handleId,
    direction: handleType === "source" ? "out" : "in",
    node: {
      id: row.id,
      kind: row.kind,
      content_type: row.content_type,
      inputs: row.inputs,
      chain_id: row.chain_id,
      chain_interface_error: row.chain_interface_error,
      chain_slots: row.chain_slots,
      chain_output_media: row.chain_output_media,
    },
  }
}

function centerOf(element: HTMLElement): { x: number; y: number } {
  const rect = element.getBoundingClientRect()
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
}

/** Радиус примагничивания к направляющей в экранных пикселях, независимо от зума. */
const GUIDE_THRESHOLD_PX = 4

const FOCUS_NODE_DURATION_MS = 200
const FOCUS_NODE_MAX_ATTEMPTS = 30

function FlowFocusNode({ nodeId }: { nodeId: number | null }) {
  const { fitView, getNode } = useReactFlow()
  const doneRef = useRef<number | null>(null)

  useEffect(() => {
    if (nodeId == null || doneRef.current === nodeId) return
    // Высота ноды появляется после измерения. Два кадра — как у «Упорядочить», дальше ждём measured.
    return pollAnimationFrames(
      () => {
        if (doneRef.current === nodeId) return true
        const node = getNode(String(nodeId))
        if (!node?.measured?.width) return false
        doneRef.current = nodeId
        void fitView({
          nodes: [{ id: String(nodeId) }],
          padding: 0.35,
          maxZoom: 1,
          duration: FOCUS_NODE_DURATION_MS,
        })
        return true
      },
      { maxAttempts: FOCUS_NODE_MAX_ATTEMPTS, startFrames: 2 },
    )
  }, [fitView, getNode, nodeId])

  return null
}

const REVEAL_MENTION_DURATION_MS = 200

function nodeOffscreen(
  node: { position: { x: number; y: number }; measured?: { width?: number; height?: number } },
  viewport: { x: number; y: number; zoom: number },
  frame: { width: number; height: number },
): boolean {
  const width = node.measured?.width ?? 0
  const height = node.measured?.height ?? 0
  if (width <= 0 || height <= 0 || frame.width <= 0 || frame.height <= 0) return false
  const left = node.position.x * viewport.zoom + viewport.x
  const top = node.position.y * viewport.zoom + viewport.y
  const right = left + width * viewport.zoom
  const bottom = top + height * viewport.zoom
  return right < 0 || bottom < 0 || left > frame.width || top > frame.height
}

/** Pan to a newly mentioned node when it sits outside the viewport. Zoom stays put. */
function FlowRevealMentioned({ nodeIds }: { nodeIds: readonly number[] }) {
  const { getNode, getViewport, setCenter } = useReactFlow()
  const frameWidth = useStore((state) => state.width)
  const frameHeight = useStore((state) => state.height)
  const revealedRef = useRef<Set<number>>(new Set())

  useEffect(() => {
    const live = new Set(nodeIds)
    for (const id of revealedRef.current) {
      if (!live.has(id)) revealedRef.current.delete(id)
    }
    if (frameWidth <= 0 || frameHeight <= 0) return
    const pending = nodeIds.filter((id) => !revealedRef.current.has(id))
    const targetId = pending[pending.length - 1]
    if (targetId == null) return

    const markRevealed = () => {
      for (const id of pending) revealedRef.current.add(id)
    }
    return pollAnimationFrames(
      () => {
        const node = getNode(String(targetId))
        const width = node?.measured?.width
        const height = node?.measured?.height
        if (!node || !width || !height) return false
        markRevealed()
        const viewport = getViewport()
        if (nodeOffscreen(node, viewport, { width: frameWidth, height: frameHeight })) {
          void setCenter(node.position.x + width / 2, node.position.y + height / 2, {
            zoom: viewport.zoom,
            duration: REVEAL_MENTION_DURATION_MS,
          })
        }
        return true
      },
      { maxAttempts: FOCUS_NODE_MAX_ATTEMPTS, onExhausted: markRevealed },
    )
  }, [frameHeight, frameWidth, getNode, getViewport, nodeIds, setCenter])

  return null
}

type FlowCanvasProps = {
  projectId: number
  rows: FlowNodeRow[]
  selectedIds: number[]
  /** Nodes named in the board composer. Visual ring only. */
  mentionedNodeIds: number[]
  focusNodeId: number | null
  /** Один раз после маунта: вписать все ноды или сначала упорядочить доску. */
  openLayout?: FlowOpenLayout
  /** Замечание к графу цепочки. Пустая строка и null — ничего не рисуем. */
  chainNotice?: string | null
  canEdit: boolean
  viewport: { x: number; y: number; zoom: number }
  onSelectIds: (ids: number[]) => void
  onSelectEdge: (id: number | null) => void
  onConnectNodes: (sourceId: number, targetId: number, ports: { sourcePort: string; targetPort: string }) => void
  onMoveNodes: (updates: { id: number; x: number; y: number; displayWidth?: number | null }[]) => void
  onResizeNode: (id: number, width: number, position: { x: number; y: number }) => void
  onViewportChange: (viewport: { x: number; y: number; zoom: number }) => void
  onZoom: (zoom: number) => void
  onPaneMenu: (clientX: number, clientY: number, worldX: number, worldY: number) => void
  onConnectDropMenu: (
    clientX: number,
    clientY: number,
    worldX: number,
    worldY: number,
    connectFrom: FlowMenuConnectFrom,
  ) => void
  onNodeMenu: (clientX: number, clientY: number, nodeId: number) => void
  onPlaceFiles: (files: File[], worldX: number, worldY: number, zoom: number) => void
  connectPreview: FlowConnectPreview | null
}

function rowsToEdges(rows: FlowNodeRow[]): Edge[] {
  const ids = new Set(rows.map((row) => row.id))
  const edges: Edge[] = []
  for (const row of rows) {
    for (const input of row.inputs ?? []) {
      if (!ids.has(input.source_node_id)) continue
      edges.push({
        id: String(input.id),
        source: String(input.source_node_id),
        target: String(row.id),
        sourceHandle: input.source_port ?? "image",
        targetHandle: flowVisualTargetHandle(row.kind, input.target_port, row),
      })
    }
  }
  return edges
}

function rowsToNodes(rows: FlowNodeRow[], selectedIds: ReadonlySet<number>, canEdit: boolean): FlowCardNode[] {
  return rows.map((row) => ({
    id: String(row.id),
    type: "flowCard",
    position: { x: row.position_x, y: row.position_y },
    width: flowNodeWidth(flowNodeAspectRatio(row), row.display_width),
    data: row,
    selected: selectedIds.has(row.id),
    draggable: canEdit,
  }))
}

/** Ring for `@` mentions. Does not touch `selected`, so the node chat stays closed. */
function applyMentioned(nodes: FlowCardNode[], mentionedIds: ReadonlySet<number>): FlowCardNode[] {
  let changed = false
  const next = nodes.map((node) => {
    const mentioned = mentionedIds.has(Number(node.id))
    if (Boolean(node.data.mentioned) === mentioned) return node
    changed = true
    return { ...node, data: { ...node.data, mentioned } }
  })
  return changed ? next : nodes
}

function sameFlowNodes(current: FlowCardNode[], next: FlowCardNode[]): boolean {
  if (current.length !== next.length) return false
  for (let index = 0; index < current.length; index += 1) {
    const left = current[index]
    const right = next[index]
    if (!left || !right) return false
    if (left.id !== right.id || left.selected !== right.selected || left.draggable !== right.draggable) return false
    if (left.data !== right.data || left.width !== right.width) return false
    if (left.position.x !== right.position.x || left.position.y !== right.position.y) return false
  }
  return true
}

function FlowChainNotice({ message }: { message: string }) {
  return (
    <Panel
      position="bottom-left"
      role="status"
      className="pointer-events-auto flex w-max max-w-[min(24rem,calc(100%-13rem))] items-start gap-2 rounded-lg border border-ui-border bg-[color-mix(in_srgb,var(--ui-bg-elevated)_72%,transparent)] px-3 py-2 text-sm text-ui-text-primary shadow-sm backdrop-blur-md"
      onPointerDown={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
    >
      <TriangleAlert className="mt-0.5 size-4 shrink-0 text-ui-warning" aria-hidden />
      <p className="min-w-0 text-pretty">{message}</p>
    </Panel>
  )
}

export function FlowCanvas({ projectId, ...props }: FlowCanvasProps) {
  return (
    <FlowProjectContext.Provider value={projectId}>
      <ReactFlowProvider>
        <FlowCanvasInner {...props} />
      </ReactFlowProvider>
    </FlowProjectContext.Provider>
  )
}

function FlowCanvasInner({
  rows,
  selectedIds,
  mentionedNodeIds,
  focusNodeId,
  openLayout,
  chainNotice = null,
  canEdit,
  viewport,
  onSelectIds,
  onSelectEdge,
  onConnectNodes,
  onMoveNodes,
  onResizeNode,
  onViewportChange,
  onZoom,
  onPaneMenu,
  onConnectDropMenu,
  onNodeMenu,
  onPlaceFiles,
  connectPreview,
}: Omit<FlowCanvasProps, "projectId">) {
  const { t } = useTranslation("flow")
  const theme = useUiStore((state) => state.theme)
  const { screenToFlowPosition, getNodes, getZoom } = useReactFlow<FlowCardNode>()
  // Стартовые массивы считаем один раз: доска перерисовывается на каждый кадр перетаскивания.
  const [initialNodes] = useState(() => rowsToNodes(rows, new Set(selectedIds), canEdit))
  const [initialEdges] = useState(() => rowsToEdges(rows))
  const [nodes, setNodes, onNodesChange] = useNodesState<FlowCardNode>(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(initialEdges)
  // Ребро должно быть в том же кадре, где xyflow снимает линию жеста. Эффект после paint мигал бы.
  const [edgeRows, setEdgeRows] = useState(rows)
  if (rows !== edgeRows) {
    setEdgeRows(rows)
    setEdges((current) => {
      const selected = new Set(current.filter((edge) => edge.selected).map((edge) => edge.id))
      return rowsToEdges(rows).map((edge) => ({ ...edge, selected: selected.has(edge.id) }))
    })
  }
  const [guides, setGuides] = useState<FlowGuide[]>([])
  const rowsRef = useRef(rows)
  rowsRef.current = rows
  const structureRef = useRef({ rows, selectedIds, canEdit })
  // xyflow перезапускает SelectionListener при новой ссылке onSelectionChange и зовёт её со
  // старым выделением. Стабильный колбэк не зависит от того, как родитель передаёт сеттеры.
  const onSelectIdsRef = useRef(onSelectIds)
  onSelectIdsRef.current = onSelectIds
  const onSelectEdgeRef = useRef(onSelectEdge)
  onSelectEdgeRef.current = onSelectEdge
  const snappingDrag = useRef(false)
  const resizeSnap = useRef<{ id: string; anchor: FlowResizeAnchor; rect: FlowRect } | null>(null)
  const viewportTimer = useRef<number | null>(null)
  // Тот же фильтр, что у panActivationKeyCode: пробел в поле ввода ладонь не включает.
  const spacePan = useKeyPress("Space")
  const spacePanRef = useRef(spacePan)
  spacePanRef.current = spacePan
  const boardRef = useRef<HTMLDivElement>(null)
  const pointerRef = useRef<{ x: number; y: number } | null>(null)
  const [fileDragOver, setFileDragOver] = useState(false)
  const ignorePaneClick = useRef(false)

  useEffect(() => {
    const rowsChanged = structureRef.current.rows !== rows
    const selectedIdsChanged = structureRef.current.selectedIds !== selectedIds
    const structureChanged = rowsChanged || selectedIdsChanged || structureRef.current.canEdit !== canEdit
    structureRef.current = { rows, selectedIds, canEdit }
    const mentioned = new Set(mentionedNodeIds)
    const draggable = canEdit && !spacePanRef.current
    setNodes((current) => {
      // Клик по ноде выделяет её в xyflow раньше, чем слушатель запишет id родителю.
      // Если в том же кадре пришли новые данные, родительское выделение ещё старое:
      // запись selected:false затирает клик, слушатель отвечает новым id, и кадры
      // чередуются, пока React не оборвёт цикл.
      const selected =
        rowsChanged && !selectedIdsChanged
          ? new Set(current.flatMap((node) => (node.selected ? [Number(node.id)] : [])))
          : new Set(selectedIds)
      // Только упоминание: не пересобирать позиции, иначе сдвиг карточки сбросится.
      const base = structureChanged ? rowsToNodes(rows, selected, draggable) : current
      const next = applyMentioned(base, mentioned)
      return sameFlowNodes(current, next) ? current : next
    })
  }, [canEdit, mentionedNodeIds, rows, selectedIds, setNodes])

  // Только флаг: полный rowsToNodes сбросил бы несохранённую позицию карточки.
  useEffect(() => {
    const draggable = canEdit && !spacePan
    setNodes((current) => {
      if (current.every((node) => node.draggable === draggable)) return current
      return current.map((node) => ({ ...node, draggable }))
    })
  }, [canEdit, setNodes, spacePan])

  useEffect(() => {
    return () => {
      if (viewportTimer.current != null) window.clearTimeout(viewportTimer.current)
    }
  }, [])

  const snapDraggedNodes = useCallback(
    (changes: NodeChange<FlowCardNode>[]): NodeChange<FlowCardNode>[] => {
      const positions = changes.filter(
        (change): change is NodePositionChange => change.type === "position" && change.position != null,
      )
      if (positions.length === 0) return changes
      const movingIds = new Set(positions.map((change) => change.id))
      const proposed = new Map(positions.map((change) => [change.id, change.position]))
      const dragged: FlowRect[] = []
      const others: FlowRect[] = []
      for (const node of getNodes()) {
        const width = node.measured?.width
        const height = node.measured?.height
        if (width == null || height == null) continue
        const next = proposed.get(node.id)
        const rect = { x: next?.x ?? node.position.x, y: next?.y ?? node.position.y, width, height }
        if (movingIds.has(node.id)) dragged.push(rect)
        else others.push(rect)
      }
      if (dragged.length === 0) return changes
      const snap = flowGroupAlignmentGuides(dragged, others, GUIDE_THRESHOLD_PX / getZoom())
      setGuides(snap.guides)
      if (snap.dx === 0 && snap.dy === 0) return changes
      return changes.map((change) => {
        if (change.type !== "position" || !change.position) return change
        return { ...change, position: { x: change.position.x + snap.dx, y: change.position.y + snap.dy } }
      })
    },
    [getNodes, getZoom],
  )

  const snapResizingChanges = useCallback(
    (changes: NodeChange<FlowCardNode>[]): NodeChange<FlowCardNode>[] => {
      const dimension = changes.find(
        (change) => change.type === "dimensions" && change.resizing === true && change.dimensions,
      )
      if (!dimension || dimension.type !== "dimensions" || !dimension.dimensions) return changes
      const node = getNodes().find((item) => item.id === dimension.id)
      const width = node?.measured?.width
      const height = node?.measured?.height
      if (!node || width == null || height == null) return changes

      const position = changes.find((change) => change.type === "position" && change.id === dimension.id)
      const proposed = {
        x: position?.type === "position" && position.position ? position.position.x : node.position.x,
        y: position?.type === "position" && position.position ? position.position.y : node.position.y,
        width: dimension.dimensions.width,
        height: dimension.dimensions.height,
      }
      const others: FlowRect[] = []
      for (const item of getNodes()) {
        if (item.id === dimension.id) continue
        const itemWidth = item.measured?.width
        const itemHeight = item.measured?.height
        if (itemWidth == null || itemHeight == null) continue
        others.push({ x: item.position.x, y: item.position.y, width: itemWidth, height: itemHeight })
      }
      const widthShare = Math.min(1, clampFlowNodeRatio(flowNodeAspectRatio(node.data)))
      const locked = resizeSnap.current?.id === dimension.id ? resizeSnap.current.anchor : null
      const snap = flowResizeSnap(
        proposed,
        { x: node.position.x, y: node.position.y, width, height },
        others,
        GUIDE_THRESHOLD_PX / getZoom(),
        {
          minWidth: FLOW_NODE_MIN_LONG_SIDE * widthShare,
          maxWidth: FLOW_NODE_MAX_LONG_SIDE * widthShare,
        },
        locked,
      )
      if (snap.anchor) resizeSnap.current = { id: dimension.id, anchor: snap.anchor, rect: snap.rect }
      setGuides(snap.guides)

      const next = changes.map((change) => {
        if (change.type === "dimensions" && change.id === dimension.id && change.dimensions) {
          return { ...change, dimensions: { width: snap.rect.width, height: snap.rect.height } }
        }
        if (change.type === "position" && change.id === dimension.id && change.position) {
          return { ...change, position: { x: snap.rect.x, y: snap.rect.y } }
        }
        return change
      })
      const hasPosition = next.some((change) => change.type === "position" && change.id === dimension.id)
      if (!hasPosition && (snap.rect.x !== proposed.x || snap.rect.y !== proposed.y)) {
        next.push({ id: dimension.id, type: "position", position: { x: snap.rect.x, y: snap.rect.y } })
      }
      return next
    },
    [getNodes, getZoom],
  )

  const handleNodesChange = useCallback(
    (changes: NodeChange<FlowCardNode>[]) => {
      const positions = changes.filter((change) => change.type === "position")
      if (positions.some((change) => change.dragging)) snappingDrag.current = true
      const resizing = changes.some((change) => change.type === "dimensions" && change.resizing === true)
      let next = resizing
        ? snapResizingChanges(changes)
        : positions.length > 0 && snappingDrag.current
          ? snapDraggedNodes(changes)
          : changes
      const remembered = resizeSnap.current
      if (!resizing && remembered) {
        let ended = false
        next = next.map((change) => {
          if (
            change.type === "dimensions" &&
            change.resizing === false &&
            change.id === remembered.id &&
            change.dimensions
          ) {
            ended = true
            return { ...change, dimensions: { width: remembered.rect.width, height: remembered.rect.height } }
          }
          return change
        })
        const node = getNodes().find((item) => item.id === remembered.id)
        if (ended && node && (node.position.x !== remembered.rect.x || node.position.y !== remembered.rect.y)) {
          next = [
            ...next,
            { id: remembered.id, type: "position", position: { x: remembered.rect.x, y: remembered.rect.y } },
          ]
        }
      }
      if (positions.some((change) => change.dragging === false)) {
        snappingDrag.current = false
        setGuides([])
      }
      onNodesChange(next)
      const moved: { id: number; x: number; y: number }[] = []
      for (const change of next) {
        // Левый/верхний край двигает ноду position-изменениями без dragging.
        // Прилипший кадр берём из жеста: mouseup присылает размер ресайзера без снаппа.
        if (change.type === "dimensions" && change.resizing === false && change.dimensions) {
          const snapped = resizeSnap.current?.id === change.id ? resizeSnap.current : null
          const node = getNodes().find((item) => item.id === change.id)
          if (snapped?.id === change.id) {
            resizeSnap.current = null
            setGuides([])
          }
          if (node) {
            onResizeNode(
              Number(change.id),
              Math.round(snapped?.rect.width ?? change.dimensions.width),
              snapped ? { x: snapped.rect.x, y: snapped.rect.y } : node.position,
            )
          }
        }
        if (change.type === "position" && change.dragging === false && change.position && canEdit) {
          moved.push({ id: Number(change.id), x: change.position.x, y: change.position.y })
        }
      }
      if (moved.length > 0) onMoveNodes(moved)
    },
    [canEdit, getNodes, onMoveNodes, onNodesChange, onResizeNode, snapDraggedNodes, snapResizingChanges],
  )

  // Выделение берём из стора, а не из параметров: слушатель xyflow снимает их на своём рендере, и к эффекту
  // они отстают. При монтировании он сообщал пустое выделение, хотя ноды уже пришли с фокус-нодой шота;
  // родитель откатывался на [], откатывал xyflow, тот отвечал прошлым [id] — и так до обрыва React (#185).
  const storeApi = useStoreApi<FlowCardNode>()
  const handleSelectionChange = useCallback(() => {
    const { nodeLookup, edgeLookup } = storeApi.getState()
    const ids = [...nodeLookup.values()].filter((node) => node.selected).map((node) => Number(node.id))
    const pickedEdges = [...edgeLookup.values()].filter((edge) => edge.selected)
    onSelectIdsRef.current(ids)
    if (ids.length > 0) {
      if (pickedEdges.length > 0) {
        setEdges((current) => current.map((edge) => (edge.selected ? { ...edge, selected: false } : edge)))
      }
      onSelectEdgeRef.current(null)
      return
    }
    onSelectEdgeRef.current(pickedEdges.length === 1 ? Number(pickedEdges[0]?.id) : null)
  }, [setEdges, storeApi])

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!canEdit || !connection.source || !connection.target) return
      const target = rowsRef.current.find((row) => String(row.id) === connection.target)
      if (!target) return
      const ports = flowConnectPorts(target.kind, connection.sourceHandle, connection.targetHandle, target)
      if (!ports) return
      onConnectNodes(Number(connection.source), Number(connection.target), ports)
    },
    [canEdit, onConnectNodes],
  )

  const handleConnectEnd = useCallback<OnConnectEnd>(
    (event, state) => {
      if (!canEdit || !state.fromNode || !state.fromHandle) return
      if (state.toNode != null && state.isValid === true) return
      const handleId = state.fromHandle.id
      const handleType = state.fromHandle.type
      if (!handleId || (handleType !== "source" && handleType !== "target")) return
      const row = rowsRef.current.find((item) => String(item.id) === state.fromNode?.id)
      if (!row) return
      const point = connectEndClientPoint(event)
      if (!point) return
      const hitId = flowNodeIdAtPoint(point.x, point.y)
      if (hitId != null) {
        const resolved = flowResolveNodeDrop(rowsRef.current, { id: row.id, handleType, handleId }, hitId)
        if (!resolved) return
        const target = rowsRef.current.find((item) => item.id === resolved.targetId)
        if (!target) return
        const ports = flowConnectPorts(target.kind, resolved.sourceHandle, resolved.targetHandle, target)
        if (!ports) return
        onConnectNodes(resolved.sourceId, resolved.targetId, ports)
        return
      }
      const connectFrom = connectMenuFromHandle(row, handleType, handleId)
      if (!connectFrom) return
      // Клик по панели в том же жесте закрыл бы меню, как после рамки выделения.
      ignorePaneClick.current = true
      window.setTimeout(() => {
        ignorePaneClick.current = false
      }, 0)
      const world = screenToFlowPosition(point)
      onConnectDropMenu(point.x, point.y, world.x, world.y, connectFrom)
    },
    [canEdit, onConnectDropMenu, onConnectNodes, screenToFlowPosition],
  )

  const validConnection = useCallback((connection: Connection | Edge) => {
    return flowConnectionAllowed(rowsRef.current, connection)
  }, [])

  const handleMove = useCallback<OnMove>(
    (_event, next) => {
      onZoom(next.zoom)
    },
    [onZoom],
  )

  const handleMoveEnd = useCallback<OnMove>(
    (_event, next) => {
      onZoom(next.zoom)
      if (!canEdit) return
      if (viewportTimer.current != null) window.clearTimeout(viewportTimer.current)
      viewportTimer.current = window.setTimeout(() => {
        onViewportChange({ x: next.x, y: next.y, zoom: next.zoom })
      }, 400)
    },
    [canEdit, onViewportChange, onZoom],
  )

  const openPaneMenuAt = useCallback(
    (clientX: number, clientY: number) => {
      const world = screenToFlowPosition({ x: clientX, y: clientY })
      onPaneMenu(clientX, clientY, world.x, world.y)
    },
    [onPaneMenu, screenToFlowPosition],
  )

  const handlePaneDoubleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!canEdit) return
      const target = event.target
      if (!(target instanceof Element) || target.closest(".react-flow__node")) return
      event.preventDefault()
      openPaneMenuAt(event.clientX, event.clientY)
    },
    [canEdit, openPaneMenuAt],
  )

  const placeFilesAt = useCallback(
    (clientX: number, clientY: number, files: File[]) => {
      if (!canEdit || files.length === 0) return
      const world = screenToFlowPosition({ x: clientX, y: clientY })
      onPlaceFiles(files, world.x, world.y, getZoom())
    },
    [canEdit, getZoom, onPlaceFiles, screenToFlowPosition],
  )

  const handleDragEnter = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (!event.dataTransfer || !isFileDrag(event.dataTransfer)) return
      event.preventDefault()
      if (canEdit) setFileDragOver(true)
    },
    [canEdit],
  )

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (!event.dataTransfer || !isFileDrag(event.dataTransfer)) return
      event.preventDefault()
      event.dataTransfer.dropEffect = canEdit ? "copy" : "none"
    },
    [canEdit],
  )

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    const next = event.relatedTarget
    if (next instanceof Node && event.currentTarget.contains(next)) return
    setFileDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      setFileDragOver(false)
      const transfer = event.dataTransfer
      if (!transfer || (!isFileDrag(transfer) && transfer.files.length === 0)) return
      event.preventDefault()
      placeFilesAt(event.clientX, event.clientY, Array.from(transfer.files))
    },
    [placeFilesAt],
  )

  usePasteFiles({
    targetRef: boardRef,
    enabled: canEdit,
    accept: "image",
    scope: "container",
    onFiles: (files) => {
      const board = boardRef.current
      const point = pointerRef.current ?? (board ? centerOf(board) : null)
      if (!point) return
      placeFilesAt(point.x, point.y, files)
    },
    onRejected: () => toast.error(t("referenceInputs.unsupportedFileType", { ns: "common" })),
  })

  return (
    <div
      ref={boardRef}
      className={cn(
        "flow-board relative h-full min-h-0 w-full",
        "[&_.react-flow__node]:rounded-none [&_.react-flow__node]:border-0 [&_.react-flow__node]:bg-transparent",
        "[&_.react-flow__node]:p-0 [&_.react-flow__node]:shadow-none",
        fileDragOver && "ring-2 ring-ui-accent ring-inset",
      )}
      onDoubleClick={handlePaneDoubleClick}
      onPointerMove={(event) => {
        pointerRef.current = { x: event.clientX, y: event.clientY }
      }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <ReactFlow<FlowCardNode>
        colorMode={theme}
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={handleConnect}
        onConnectEnd={handleConnectEnd}
        connectionLineComponent={FlowConnectionLine}
        isValidConnection={validConnection}
        defaultViewport={viewport}
        onMove={handleMove}
        onMoveEnd={handleMoveEnd}
        nodesConnectable={canEdit}
        edgesReconnectable={false}
        edgesFocusable
        connectionRadius={64}
        elementsSelectable
        panOnDrag={[1]}
        panActivationKeyCode="Space"
        panOnScroll
        panOnScrollMode={PanOnScrollMode.Free}
        zoomOnScroll={false}
        zoomOnDoubleClick={false}
        zoomOnPinch
        minZoom={FLOW_MIN_ZOOM}
        maxZoom={FLOW_MAX_ZOOM}
        deleteKeyCode={null}
        // Порог драга 1px, а clickDistance по умолчанию 0: сдвиг до 1px гасит click и
        // не начинает drag, поэтому быстрый клик ноду не выделяет.
        nodeClickDistance={1}
        nodeDragThreshold={1}
        selectionOnDrag
        selectionMode={SelectionMode.Partial}
        // Shift добавляет ноду к выделению. Рамка и так рисуется перетаскиванием,
        // а дефолтный selectionKeyCode Shift перехватывал бы клик по ноде.
        selectionKeyCode={null}
        multiSelectionKeyCode={["Shift", "Meta", "Control"]}
        proOptions={{ hideAttribution: true }}
        onSelectionChange={handleSelectionChange}
        onSelectionEnd={() => {
          ignorePaneClick.current = true
          window.setTimeout(() => {
            ignorePaneClick.current = false
          }, 0)
        }}
        onPaneClick={() => {
          if (ignorePaneClick.current) {
            ignorePaneClick.current = false
            return
          }
          onSelectIds([])
          onSelectEdge(null)
        }}
        onPaneContextMenu={(event) => {
          event.preventDefault()
          openPaneMenuAt(event.clientX, event.clientY)
        }}
        onNodeContextMenu={(event, node) => {
          event.preventDefault()
          onNodeMenu(event.clientX, event.clientY, Number(node.id))
        }}
        onSelectionContextMenu={(event, picked) => {
          event.preventDefault()
          event.stopPropagation()
          const node = picked[0]
          if (!node) return
          onNodeMenu(event.clientX, event.clientY, Number(node.id))
        }}
      >
        <Background />
        <FlowFocusNode nodeId={focusNodeId} />
        <FlowRevealMentioned nodeIds={mentionedNodeIds} />
        <FlowAlignmentGuides guides={guides} />
        <FlowSelectionFrame />
        {connectPreview ? <FlowConnectPreviewLine preview={connectPreview} /> : null}
        <FlowViewportControls
          rows={rows}
          selectedIds={selectedIds}
          canEdit={canEdit}
          openLayout={openLayout}
          onMoveNodes={onMoveNodes}
          onPaneMenu={onPaneMenu}
        />
        {chainNotice ? <FlowChainNotice message={chainNotice} /> : null}
      </ReactFlow>
      {rows.length === 0 ? (
        <BoardEmptyState
          icon={<Workflow />}
          title={t("empty.title")}
          hint={t("empty.hint")}
          className="pointer-events-none absolute inset-0 min-h-0"
        />
      ) : null}
    </div>
  )
}
